import { createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { baseUrl, headersDefault } from ".";
import { getAuthToken } from "../../services/authStorage";
import { axiosGetWith429Retry } from "../../services/httpRetry";
import {
  createRequestDedupe,
  SCREEN_ENTRY_THROTTLE_MS,
} from "./requestDedupe";

const storiesUrl = (path = "") => `${baseUrl}/api/v1/stories${path}`;

/**
 * The Community tray: one entry per author with a live Story, already ordered
 * by the backend (your own first, then anything unseen, then the rest).
 */
// The tray is read on mounting the Community page and again on returning to it,
// and it was the heaviest call in the 28 August incident — 48 fetches in a single
// second, which spent the member's whole rate-limit allowance and took the menu
// and the percorsi down with it. Repeats inside the window now reuse the answer.
const trayDedupe = createRequestDedupe({
  throttleMs: SCREEN_ENTRY_THROTTLE_MS,
});

export const fetchStoryTray = createAsyncThunk(
  "stories/fetchTray",
  async (_, { rejectWithValue }) => {
    try {
      return await trayDedupe.run(undefined, async () => {
        const response = await axiosGetWith429Retry(storiesUrl("/tray"), {
          headers: headersDefault(),
        });
        // `meta` carries the composer's limits — today the clip length cap, which
        // lives in the backend .env so it can change without an app release.
        return {
          groups: response.data?.data ?? [],
          meta: response.data?.meta ?? null,
        };
      });
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
  { condition: () => !trayDedupe.isFresh() },
);

/**
 * Forget the throttle so the very next read reaches the server.
 *
 * The three thunks that change the tray — publish, delete, hide — already do this
 * for themselves, so no caller has to remember. This is for anything outside them
 * that needs a guaranteed-fresh tray.
 */
export function resetStoryTrayThrottle() {
  trayDedupe.reset();
}

/**
 * Marks a Story watched. Idempotent server-side, so firing it on every open is
 * safe; viewing your own Story is deliberately not recorded.
 */
export const registerStoryView = createAsyncThunk(
  "stories/registerView",
  async ({ storyId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        storiesUrl(`/${storyId}/views`),
        {},
        { headers: headersDefault() },
      );
      return { storyId, ...response.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/**
 * Sets, swaps or clears a reaction. Passing the reaction that is already set
 * removes it; passing null clears whatever is there.
 */
export const reactToStory = createAsyncThunk(
  "stories/react",
  async ({ storyId, reaction }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        storiesUrl(`/${storyId}/reactions`),
        { reaction: reaction ?? null },
        { headers: headersDefault() },
      );
      return { storyId, ...response.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/** Who watched one of your own Stories. The backend refuses this for anyone else. */
export const fetchStoryViewers = createAsyncThunk(
  "stories/fetchViewers",
  async ({ storyId }, { rejectWithValue }) => {
    try {
      const response = await axios.get(storiesUrl(`/${storyId}/views`), {
        headers: headersDefault(),
      });
      return { storyId, ...response.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

export const deleteStory = createAsyncThunk(
  "stories/delete",
  async ({ storyId }, { rejectWithValue }) => {
    try {
      await axios.delete(storiesUrl(`/${storyId}`), {
        headers: headersDefault(),
      });
      trayDedupe.reset();
      return { storyId };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/**
 * Publishes a Story. Manual creation (Phase 3) sends `media_path` from the
 * upload endpoint; automatic ones (Phase 4) send the resolved cover and the
 * template of real values.
 */
export const publishStory = createAsyncThunk(
  "stories/publish",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axios.post(storiesUrl(), payload, {
        headers: headersDefault(),
      });
      trayDedupe.reset();
      return response.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/**
 * Asks whether an event deserves a Story, and gets the prefilled one back.
 *
 * A "no" is ordinary — most finished videos and cooked meals will not clear the
 * bar, or were already shared or declined — so callers treat a falsy
 * `should_prompt` as silence, not failure.
 */
export const checkStoryPrompt = createAsyncThunk(
  "stories/checkPrompt",
  async ({ triggerType, sourceId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        storiesUrl("/prompts/check"),
        { trigger_type: triggerType, source_id: sourceId ?? null },
        { headers: headersDefault() },
      );
      return response.data;
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/**
 * Reports a Story. Reached by holding it — there is no three-dot menu in
 * Fase 1 — and reporting the same one twice is not an error.
 */
export const reportStory = createAsyncThunk(
  "stories/report",
  async ({ storyId }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        storiesUrl(`/${storyId}/report`),
        {},
        { headers: headersDefault() },
      );
      return { storyId, ...response.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/** Moderation: take a Story out of the tray without deleting it. Badge only. */
export const setStoryHidden = createAsyncThunk(
  "stories/setHidden",
  async ({ storyId, hidden }, { rejectWithValue }) => {
    try {
      const response = await axios.patch(
        storiesUrl(`/${storyId}/hidden`),
        { hidden },
        { headers: headersDefault() },
      );
      trayDedupe.reset();
      return { storyId, ...response.data };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/** "Non ora" — the same proposal is never shown for that event again. */
export const dismissStoryPrompt = createAsyncThunk(
  "stories/dismissPrompt",
  async ({ triggerType, sourceType, sourceId }, { rejectWithValue }) => {
    try {
      await axios.post(
        storiesUrl("/prompts/dismiss"),
        {
          trigger_type: triggerType,
          source_type: sourceType ?? null,
          source_id: sourceId ?? null,
        },
        { headers: headersDefault() },
      );
      return { triggerType, sourceType, sourceId };
    } catch (e) {
      return rejectWithValue(e?.response?.data || e?.message);
    }
  },
);

/**
 * Uploads the composed 9:16 photo and returns the stored path.
 *
 * Goes through the generic upload endpoint — the field name has to match the
 * disk name, which is what tells the backend where to put it. Deliberately not
 * `headersDefault()`: that sets a JSON content type, and multipart has to set
 * its own boundary.
 */
/**
 * Uploads the Storia's media — a framed photo or a recorded clip.
 *
 * The endpoint takes both: the `master-club-story` disk accepts jpg/png as well
 * as mp4/mov/webm, and answers with the stored path either way.
 */
export const uploadStoryMedia = async (file) => {
  const body = new FormData();
  body.append("master-club-story", file);

  const response = await fetch(`${baseUrl}/api/v1/file-upload/master-club-story`, {
    method: "POST",
    headers: { Authorization: `Bearer ${getAuthToken()}` },
    body,
  });

  if (!response.ok) {
    throw new Error("Errore durante il caricamento del file.");
  }

  const data = await response.json();
  if (!data?.filename) {
    throw new Error("Errore durante il caricamento del file.");
  }

  return data.filename;
};

/** Kept so older call sites keep working; both go through the same endpoint. */
export const uploadStoryPhoto = uploadStoryMedia;
